/**
 * intake-step.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Todd L Smith <tsmith@safetyweb.org>
 */

import { AbstractControl } from "@angular/forms";
import { DataType, ServerDataType } from "../../common/data/data-types";
import { TypedData, TypedDataHolder }
  from "../../common/data/typed-data";
import { Comment } from "../../intake-form/comment";
import { AlgorithmStep, AlgorithmStepData } from "./algorithm-step";
import { IntakeAnswer } from "./intake-answer";

export interface IntakeStepData extends AlgorithmStepData
{
  order : number;
  type : DataType;
  cardinality : number;
  allowsDuplicates : boolean;
  restrictions? : any[];
  value? : any;
  comments? : Comment[];
  searchable? : boolean;
  differsFromOriginalValue? : boolean;
}

/**
 * An {@link IntakeStep} models an executable Hammurabi intake step.
 */
export class IntakeStep extends AlgorithmStep implements TypedDataHolder
{
  /**
   * The order number, used for sorting the intake steps.
   */
  public order : number;

  /**
   * The type of value required by the intake step. The allowed values are given
   * by `allowedTypes`.
   */
  public type : DataType;

  /**
   * The cardinality of the intake step. If the cardinality is greater than zero
   * (0), then it represents the exact number of answers that are required. If
   * the cardinality is zero (0), then one (1) or more answers are expected.
   */
  public cardinality : number;

  /**
   * The array of allowed values of the intake step; defaults to null if not
   * supplied. If null, then any value may be assigned.
   */
  public restrictions : any[];

  /**
   * Does the intake step permit duplicate answers?
   */
  public allowsDuplicates : boolean;

  /**
   * The initial value of the intake step; defaults to null if not supplied.
   */
  public value : any;

  /**
   * The control holds a reference to the form control this step is bound to.
   */
  public control : AbstractControl;

  /**
   * Comments that have been attached to this step
   */
  public comments : Comment[];

  /**
   * Whether the intake step is enabled and visible to the user.
   * @type {boolean}
   */
  public isActive : boolean = true;

  /**
   * Whether the intake step participates in the saving process, or, more
   * directly, whether the intake provides a state variable required by at least
   * one index. Used to add visual hints of semi-requiredness to the UI.
   * @type {boolean}
   */
  public searchable : boolean = false;

  /**
   * Indicates whether the state variable associated with this intake step has
   * been modified since the last save. This is functionally used to mark the
   * controls as dirty and trigger modified value behaviors (such as warnings
   * about saving) when the only modifications are those "inherited" from a
   * different user concurrently running the session.
   * @type {boolean}
   */
  public differsFromOriginalValue : boolean = false;

  /**
   * Provider is an ad-hoc property used to store human-readable information
   * about the last user or process that modified the step's value.
   * @type {string}
   */
  public provider? : string;

  /**
   * Construct a new {@link IntakeStep}.
   *
   * @param data
   *        An object constructed from JSON.
   */
  constructor (
    data : IntakeStepData
  )
  {
    super(data);
    this.order = data.order;
    this.type = data.type;
    this.cardinality = data.cardinality;
    this.allowsDuplicates = data.allowsDuplicates;
    this.restrictions = data.restrictions || [];
    this.comments = (data.comments || []).map(Comment.fromData);
    this.searchable = data.searchable || false;
    this.differsFromOriginalValue = data.differsFromOriginalValue || false;

    if (data.value !== undefined && data.value !== null)
    {
      // TODO: TS *without* strictNullChecks doesn't have the capacity to
      // understand that the !== undefined check informs us the optional
      // "value" property is present. Thus we have to do this TERRIBLE AWFUL
      // double type assertion. Once we turn strictNullChecks we can remove it.
      this.value = TypedData.deserialize(data as any as TypedDataHolder);
    }
    else
    {
      this.value = null;
    }
  }

  /**
   * Does the {@link IntakeStep} provide multiple answers?
   *
   * @returns
   *        `true` if the {@link IntakeStep} provides multiple answers (and
   *        `value` is therefore an array), `false` (and `value` is primitive)
   *        otherwise.
   */
  public providesMultipleAnswers () : boolean
  {
    return this.cardinality !== 1;
  }

  /**
   * Answer a {@link CompletedIntakeStep} that encapsulates the relevant
   * information from this {@link IntakeStep}.
   *
   * @returns
   *        A {@link CompletedIntakeStep}.
   */
  public completedIntakeStep () : IntakeAnswer
  {
    const value : any = TypedData.serialize(this);

    return new IntakeAnswer(this.id, value);
  }

  /**
   * Answer the lower bound of the {@link IntakeStep}'s numeric type.
   */
  public lowerBound () : number
  {
    switch (this.type)
    {
      case "whole number":
        return 0;
      case "natural number":
        return 1;
      case "integer":
      case "float":
        return -Infinity;
      default:
        throw new Error("semantic type is nonnumeric");
    }
  }

  public static ordered (step : IntakeStep) : boolean
  {
    return step.order !== Number.MAX_SAFE_INTEGER;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/structure/intake-step.ts